import { differenceInCalendarDays, format, isValid, parseISO } from 'date-fns';

const MONTH_FORMAT = 'MMM d';
const FULL_FORMAT = 'MMM d, yyyy';

export const parseDateSafe = (value) => {
  if (!value) return null;
  if (value instanceof Date) {
    return isValid(value) ? value : null;
  }
  const parsed = parseISO(String(value));
  return isValid(parsed) ? parsed : null;
};

export const formatDeadlineDate = (date, now = new Date()) => {
  if (!date) return 'Invalid deadline';
  const sameYear = date.getFullYear() === now.getFullYear();
  return format(date, sameYear ? MONTH_FORMAT : FULL_FORMAT);
};

export const calculateWorkingDays = (start, end) => {
  if (!start || !end) return null;
  let count = 0;
  const startDate = new Date(start);
  const endDate = new Date(end);
  const increment = startDate <= endDate ? 1 : -1;
  const current = new Date(startDate);

  while ((increment > 0 && current <= endDate) || (increment < 0 && current >= endDate)) {
    const day = current.getDay();
    if (day !== 0 && day !== 6) {
      count += increment;
    }
    current.setDate(current.getDate() + increment);
  }

  return count;
};

export const calculateProjectedCompletionDate = (startDateInput, estimatedHours, spentHours, now = new Date()) => {
  if (!startDateInput || !estimatedHours || estimatedHours <= 0) return null;
  const startDate = parseDateSafe(startDateInput);
  if (!startDate) return null;
  const elapsedDays = Math.max(differenceInCalendarDays(now, startDate), 1);
  if (!spentHours || spentHours <= 0) return null;
  const dailyVelocity = spentHours / elapsedDays;
  if (dailyVelocity <= 0) return null;
  const remainingHours = Math.max(estimatedHours - spentHours, 0);
  if (remainingHours === 0) return now;
  const daysToComplete = Math.ceil(remainingHours / dailyVelocity);
  if (!Number.isFinite(daysToComplete)) return null;
  const completion = new Date(now);
  completion.setDate(completion.getDate() + daysToComplete);
  return completion;
};

const getDeadlineText = (daysLeft) => {
  if (daysLeft === 0) return 'Due today';
  if (daysLeft === 1) return 'Due tomorrow';
  if (daysLeft === -1) return '1 day overdue';
  if (daysLeft < 0) return `${Math.abs(daysLeft)} days overdue`;
  if (daysLeft > 365) {
    const months = Math.round(daysLeft / 30);
    return `Due in ${months} month${months === 1 ? '' : 's'}`;
  }
  return `Due in ${daysLeft} days`;
};

const baseStatusMap = {
  critical: { label: 'Critical', color: '#ef4444', background: '#fee2e2', icon: '❌' },
  urgent: { label: 'Due Soon', color: '#ef4444', background: '#fee2e2', icon: '🚨' },
  warning: { label: 'At Risk', color: '#f97316', background: '#ffedd5', icon: '⚠️' },
  soon: { label: 'Due Soon', color: '#f59e0b', background: '#fef3c7', icon: '⏰' },
  normal: { label: 'On Track', color: '#6b7280', background: '#f3f4f6', icon: '📅' },
  missing: { label: 'No deadline', color: '#6b7280', background: '#f3f4f6', icon: '📄' }
};

export const calculateDeadlineStatus = (dueDateInput, budgetPercentage = 0, now = new Date()) => {
  const dueDate = parseDateSafe(dueDateInput);
  if (!dueDate) {
    const status = baseStatusMap.missing;
    return {
      daysLeft: null,
      isOverdue: false,
      urgency: 'none',
      icon: status.icon,
      color: status.color,
      background: status.background,
      statusLabel: status.label,
      text: 'No deadline set',
      formattedDate: null,
      warning: null,
      dueDate: null,
      ariaLabel: 'Deadline: No deadline set'
    };
  }

  const daysLeft = differenceInCalendarDays(dueDate, now);
  let urgency;

  if (daysLeft < 0) urgency = 'critical';
  else if (daysLeft <= 1) urgency = 'urgent';
  else if (daysLeft <= 3) urgency = 'warning';
  else if (daysLeft <= 7) urgency = 'soon';
  else urgency = 'normal';

  let status = { ...baseStatusMap[urgency] };
  const formattedDate = formatDeadlineDate(dueDate, now);
  const text = getDeadlineText(daysLeft);
  let warning = null;

  if (budgetPercentage >= 100) {
    status = { ...baseStatusMap.critical, icon: '🚨', label: 'Critical' };
    warning = 'Over budget';
  } else if (budgetPercentage >= 90 && daysLeft <= 3) {
    status = { ...baseStatusMap.critical };
    warning = 'Budget and deadline at risk';
  }

  const ariaLabel = `Deadline: ${text}${warning ? `, Warning: ${warning}` : ''}, Status: ${status.label}`;

  return {
    daysLeft,
    isOverdue: daysLeft < 0,
    urgency,
    icon: status.icon,
    color: status.color,
    background: status.background,
    statusLabel: status.label,
    text,
    formattedDate,
    warning,
    ariaLabel,
    dueDate
  };
};

export const getDeadlineSortPriority = (project, now = new Date()) => {
  const dueDate = project?.timeline?.due_date;
  if (!dueDate) return 5;
  const status = calculateDeadlineStatus(dueDate, project?.time_percent || 0, now);
  if (status.daysLeft < 0) return 0;
  if (status.daysLeft === 0) return 1;
  if (status.daysLeft <= 3) return 2;
  if (status.daysLeft <= 7) return 3;
  return 4;
};

export const buildDeadlineTooltip = ({ dueDate, status, workingDays, projectedCompletion }) => {
  const parts = [];
  if (dueDate) {
    parts.push(`Exact deadline: ${format(dueDate, FULL_FORMAT)}`);
  }
  if (workingDays != null) {
    parts.push(`Working days remaining: ${workingDays}`);
  }
  if (projectedCompletion) {
    parts.push(`Projected completion: ${format(projectedCompletion, FULL_FORMAT)}`);
  }
  if (status.warning) {
    parts.push(`Warning: ${status.warning}`);
  }
  return parts.join('\n');
};

